import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import { useEventCallback } from "usehooks-ts";

import { SnackbarSeverity } from "./messages/Message";
import { Messages, ShowOptions, SnackbarMessage } from "./messages/Messages";

type SnackbarContextValue = (
  severity: SnackbarSeverity,
  message: ReactNode,
  options?: ShowOptions,
) => void;

const SnackbarContext = createContext<SnackbarContextValue | null>(null);

interface SnackbarProviderProps extends PropsWithChildren {
  autoHideDuration?: number;
  maxMessages?: number;
}

export const SnackbarProvider = (props: SnackbarProviderProps) => {
  const { children, autoHideDuration = 4000, maxMessages = 3 } = props;

  const [messages, setMessages] = useState<SnackbarMessage[]>([]);

  const showSnackbar = useCallback(
    (severity: SnackbarSeverity, message: ReactNode, options = {}) => {
      setMessages((queue) => {
        const newQueue = [
          { severity, message, options, key: crypto.randomUUID() },
          ...queue,
        ];

        const nextQueue = newQueue.slice(0, maxMessages);

        return nextQueue;
      });
    },
    [maxMessages],
  );

  const handleRemoveMessage = useEventCallback(
    (key: SnackbarMessage["key"]) => {
      setMessages((queue) => queue.filter((msg) => msg.key !== key));
    },
  );

  return (
    <SnackbarContext.Provider value={showSnackbar}>
      <Messages
        messages={messages}
        onRemoveMessage={handleRemoveMessage}
        autoHideDuration={autoHideDuration}
      />
      {children}
    </SnackbarContext.Provider>
  );
};

export const useSnackbarContext = () => useContext(SnackbarContext);
